import { KeyboardEvent } from 'react';

export interface KeyboardActivateProps {
  role: 'button';
  tabIndex: 0;
  onKeyDown: (event: KeyboardEvent) => void;
}

/**
 * Props ARIA per rendere un elemento non nativamente interattivo (es. `<div>`)
 * attivabile anche da tastiera (Enter/Space), oltre che da click.
 *
 * Se `onActivate` non è definito (azione non disponibile, es. count === 0),
 * ritorna un oggetto vuoto: l'elemento resta non focalizzabile e non viene
 * annunciato come bottone dalla tecnologia assistiva.
 *
 * Va chiamato incondizionatamente in cima al componente (regole degli hook);
 * è `onActivate` a poter essere `undefined` a seconda dello stato.
 *
 * @example
 * const onActivate = hasItems ? openModal : undefined;
 * const activateProps = useKeyboardActivate(onActivate);
 * <div className={hasItems ? styles.clickable : ''} onClick={onActivate} {...activateProps} />
 */
export function useKeyboardActivate(
  onActivate?: () => void
): Partial<KeyboardActivateProps> {
  if (!onActivate) return {};

  return {
    role: 'button',
    tabIndex: 0,
    onKeyDown: (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onActivate();
      }
    },
  };
}
